/**
 * Bids Duck
 *
 * Manages bids data from Sharetribe listings with Redux caching.
 * Handles creating, fetching, updating, and deleting bids.
 */

import { createSlice } from '@reduxjs/toolkit';
import { storableError } from '../util/errors';
import * as log from '../util/log';
import {
  getBids as getBidsAPI,
  createBid as createBidAPI,
  updateBid as updateBidAPI,
  deleteBid as deleteBidAPI,
} from '../util/api';
import { LISTING_TYPES } from '../util/listingTypes';
import { createImageVariantConfig } from '../util/sdkLoader';
import { denormalisedResponseEntities } from '../util/data';

export const initialState = {
  allBids: [],
  userBids: [],
  crewBids: {}, // Keyed by crew_id
  crewBidsFetchedAt: {},
  fetchAllBidsInProgress: false,
  fetchAllBidsError: null,
  fetchUserBidsInProgress: false,
  fetchUserBidsError: null,
  fetchCrewBidsInProgress: false,
  fetchCrewBidsError: null,
  createBidInProgress: false,
  createBidError: null,
  updateBidInProgress: false,
  updateBidError: null,
  deleteBidInProgress: false,
  deleteBidError: null,
  lastFetchTime: null, // Track when we last fetched data
};

const replaceBid = (bids, nextBid) => bids.map(bid => (bid.id === nextBid.id ? nextBid : bid));

const bidsSlice = createSlice({
  name: 'bids',
  initialState,
  reducers: {
    fetchAllBidsRequest(state) {
      state.fetchAllBidsInProgress = true;
      state.fetchAllBidsError = null;
    },
    fetchAllBidsSuccess(state, action) {
      state.allBids = action.payload.bids;
      state.fetchAllBidsInProgress = false;
      state.lastFetchTime = Date.now();
    },
    fetchAllBidsError(state, action) {
      state.fetchAllBidsInProgress = false;
      state.fetchAllBidsError = action.payload;
    },
    fetchUserBidsRequest(state) {
      state.fetchUserBidsInProgress = true;
      state.fetchUserBidsError = null;
    },
    fetchUserBidsSuccess(state, action) {
      state.userBids = action.payload.bids;
      state.fetchUserBidsInProgress = false;
    },
    fetchUserBidsError(state, action) {
      state.fetchUserBidsInProgress = false;
      state.fetchUserBidsError = action.payload;
    },
    fetchCrewBidsRequest(state) {
      state.fetchCrewBidsInProgress = true;
      state.fetchCrewBidsError = null;
    },
    fetchCrewBidsSuccess(state, action) {
      state.crewBids[action.payload.crewId] = action.payload.bids;
      state.crewBidsFetchedAt[action.payload.crewId] = Date.now();
      state.fetchCrewBidsInProgress = false;
    },
    fetchCrewBidsError(state, action) {
      state.fetchCrewBidsInProgress = false;
      state.fetchCrewBidsError = action.payload;
    },
    createBidRequest(state) {
      state.createBidInProgress = true;
      state.createBidError = null;
    },
    createBidSuccess(state, action) {
      state.allBids.unshift(action.payload.bid);
      state.userBids.unshift(action.payload.bid);
      state.createBidInProgress = false;
    },
    createBidError(state, action) {
      state.createBidInProgress = false;
      state.createBidError = action.payload;
    },
    updateBidRequest(state) {
      state.updateBidInProgress = true;
      state.updateBidError = null;
    },
    updateBidSuccess(state, action) {
      state.allBids = replaceBid(state.allBids, action.payload.bid);
      state.userBids = replaceBid(state.userBids, action.payload.bid);
      state.updateBidInProgress = false;
    },
    updateBidError(state, action) {
      state.updateBidInProgress = false;
      state.updateBidError = action.payload;
    },
    deleteBidRequest(state) {
      state.deleteBidInProgress = true;
      state.deleteBidError = null;
    },
    deleteBidSuccess(state, action) {
      const deletedBid = action.payload.bid;
      const bidId = deletedBid?.id || action.payload.bidId;
      state.allBids = state.allBids.filter(bid => bid.id !== bidId);
      state.userBids = state.userBids.map(bid =>
        bid.id === bidId ? { ...bid, ...deletedBid, status: 'deleted' } : bid
      );
      state.deleteBidInProgress = false;
    },
    deleteBidError(state, action) {
      state.deleteBidInProgress = false;
      state.deleteBidError = action.payload;
    },
    clearBids() {
      return initialState;
    },
  },
});

const {
  fetchAllBidsRequest: fetchAllBidsRequestAction,
  fetchAllBidsSuccess: fetchAllBidsSuccessAction,
  fetchAllBidsError: fetchAllBidsErrorAction,
  fetchUserBidsRequest: fetchUserBidsRequestAction,
  fetchUserBidsSuccess: fetchUserBidsSuccessAction,
  fetchUserBidsError: fetchUserBidsErrorAction,
  fetchCrewBidsRequest: fetchCrewBidsRequestAction,
  fetchCrewBidsSuccess: fetchCrewBidsSuccessAction,
  fetchCrewBidsError: fetchCrewBidsErrorAction,
  createBidRequest: createBidRequestAction,
  createBidSuccess: createBidSuccessAction,
  createBidError: createBidErrorAction,
  updateBidRequest: updateBidRequestAction,
  updateBidSuccess: updateBidSuccessAction,
  updateBidError: updateBidErrorAction,
  deleteBidRequest: deleteBidRequestAction,
  deleteBidSuccess: deleteBidSuccessAction,
  deleteBidError: deleteBidErrorAction,
} = bidsSlice.actions;

export const { clearBids } = bidsSlice.actions;
export default bidsSlice.reducer;

const fetchAllBidsRequest = () => fetchAllBidsRequestAction();
const fetchAllBidsSuccess = bids => fetchAllBidsSuccessAction({ bids });
const fetchAllBidsError = error => fetchAllBidsErrorAction(error);

const fetchUserBidsRequest = () => fetchUserBidsRequestAction();
const fetchUserBidsSuccess = bids => fetchUserBidsSuccessAction({ bids });
const fetchUserBidsError = error => fetchUserBidsErrorAction(error);

const fetchCrewBidsRequest = () => fetchCrewBidsRequestAction();
const fetchCrewBidsSuccess = (crewId, bids) => fetchCrewBidsSuccessAction({ crewId, bids });
const fetchCrewBidsError = error => fetchCrewBidsErrorAction(error);

const createBidRequest = () => createBidRequestAction();
const createBidSuccess = bid => createBidSuccessAction({ bid });
const createBidError = error => createBidErrorAction(error);

const updateBidRequest = () => updateBidRequestAction();
const updateBidSuccess = bid => updateBidSuccessAction({ bid });
const updateBidError = error => updateBidErrorAction(error);

const deleteBidRequest = () => deleteBidRequestAction();
const deleteBidSuccess = bid => deleteBidSuccessAction({ bidId: bid?.id, bid });
const deleteBidError = error => deleteBidErrorAction(error);

// ================ Selectors ================ //

export const getAllBids = state => state.bids?.allBids || [];
export const getUserBids = state => state.bids?.userBids || [];
export const getCrewBids = (state, crewId) => state.bids?.crewBids[crewId] || [];
export const getCrewBidsFetchedAt = (state, crewId) =>
  state.bids?.crewBidsFetchedAt?.[crewId] || null;
export const getFeaturedBids = state => state.bids?.allBids.slice(0, 3) || [];
export const isFetchingBids = state => state.bids?.fetchAllBidsInProgress || false;
export const getBidsError = state => state.bids?.fetchAllBidsError || null;
export const getLastFetchTime = state => state.bids?.lastFetchTime || null;

// ================ Thunks ================ //

/**
 * Fetch all bids with caching
 * Only fetches if data is stale (older than 30 seconds) or not yet fetched
 */
export const fetchAllBids = (force = false) => (dispatch, getState, sdk) => {
  const state = getState();
  const lastFetchTime = getLastFetchTime(state);
  const CACHE_DURATION = 30 * 1000; // 30 seconds

  // Check if we need to fetch (no data, stale data, or forced refresh)
  if (!force && lastFetchTime && Date.now() - lastFetchTime < CACHE_DURATION) {
    return Promise.resolve(getAllBids(state));
  }

  dispatch(fetchAllBidsRequest());

  // We have migrated Bids to Supabase, so we fetch directly using getBidsAPI
  const fetchPromise = getBidsAPI({ limit: 100 }).then(response => {
    // API returns { bids: [...] } or data.bids
    return response?.bids || response?.data?.bids || [];
  });

  return fetchPromise
    .then(bids => {
      dispatch(fetchAllBidsSuccess(bids));
      return bids;
    })
    .catch(e => {
      log.error(e, 'fetch-all-bids-failed');
      dispatch(fetchAllBidsError(storableError(e)));
      throw e;
    });
};

/**
 * Transform a Sharetribe listing to bid format for display
 */
const transformListingToBid = listing => {
  const attrs = listing.attributes || {};
  const publicData = attrs.publicData || {};
  const isBidMarkedActive = publicData.active !== false;

  const images = listing.images || [];
  const firstImage = images[0];

  // Get image URL - try different variants
  let imageUrl = '';
  if (firstImage) {
    imageUrl =
      firstImage.attributes?.variants?.['listing-card']?.url ||
      firstImage.attributes?.variants?.['scaled-medium']?.url ||
      firstImage.attributes?.variants?.['scaled-small']?.url ||
      '';
  }

  // Get listing ID (handle UUID object)
  const listingId = listing.id?.uuid || listing.id?.id?.uuid || listing.id || '';

  // Prices are stored in cents in Sharetribe publicData, convert to dollars for UI display
  const minPriceCents = publicData.min_price || 0;
  const maxPriceCents = publicData.max_price || 0;
  const minPriceDisplay = minPriceCents / 100;
  const maxPriceDisplay = maxPriceCents / 100;

  // Get buyer's message from publicData
  let buyersMessage = null;
  const possibleKeys = [
    'message',
    'buyersMessage',
    'buyers_message',
    'Buyers message',
    'Buyer message',
    "Buyer's message",
    'buyerMessage',
    'buyer_message',
  ];

  for (const key of possibleKeys) {
    if (
      publicData[key] &&
      typeof publicData[key] === 'string' &&
      publicData[key].trim().length > 0
    ) {
      buyersMessage = publicData[key].trim();
      break;
    }
  }

  return {
    id: listingId,
    title: attrs.title || '',
    date: publicData.event_date || publicData.eventDate || publicData.eventDateTime || '',
    venue: publicData.venue || publicData.venueName || '',
    location:
      publicData.event_location ||
      publicData.eventLocation ||
      publicData.venueLocation ||
      publicData.location ||
      '',
    numberOfTickets: publicData.number_of_tickets || publicData.numberOfTickets || 1,
    currentBid: minPriceDisplay,
    maxBid: maxPriceDisplay,
    image: imageUrl,
    description: attrs.description || '',
    buyersMessage: buyersMessage,
    status: isBidMarkedActive
      ? attrs.state === 'published'
        ? 'active'
        : attrs.state === 'closed'
        ? 'archived'
        : attrs.state || 'active'
      : 'archived',
    active: isBidMarkedActive,
    eventType: publicData.ticket_type || publicData.ticketType || '',
    listing: listing, // Store original listing for reference
  };
};

/**
 * Fetch user's bids from Sharetribe listings
 * @param {string} userId - The user ID (UUID string)
 */
export const fetchUserBids = userId => (dispatch, getState, sdk) => {
  if (!userId) {
    return Promise.resolve([]);
  }

  if (!sdk) {
    console.error('fetchUserBids: SDK not available');
    return Promise.resolve([]);
  }

  dispatch(fetchUserBidsRequest());

  // We have migrated Bids to Supabase, so we fetch directly using getBidsAPI
  return getBidsAPI({ creator_id: userId, limit: 100 })
    .then(response => {
      const bids = response?.bids || response?.data?.bids || [];

      dispatch(fetchUserBidsSuccess(bids));
      return bids;
    })
    .catch(e => {
      log.error(e, 'fetch-user-bids-failed');
      dispatch(fetchUserBidsError(storableError(e)));
      throw e;
    });
};

/**
 * Fetch bids for a specific crew
 */
const CREW_BIDS_CACHE_DURATION = 2 * 60 * 1000;
const crewBidsRequests = new Map();

export const fetchCrewBids = (crewIdOrName, fetchOptions = {}) => (dispatch, getState) => {
  if (!crewIdOrName) {
    return Promise.resolve([]);
  }

  const { force = false } = fetchOptions;
  const cacheKey = String(crewIdOrName);

  // Check if we already have this crew's bids cached
  const state = getState();
  const cachedBids = getCrewBids(state, cacheKey);
  const fetchedAt = getCrewBidsFetchedAt(state, cacheKey);
  const hasFreshCache =
    Array.isArray(cachedBids) && fetchedAt && Date.now() - fetchedAt < CREW_BIDS_CACHE_DURATION;

  if (!force && hasFreshCache) {
    return Promise.resolve(cachedBids);
  }

  if (!force && crewBidsRequests.has(cacheKey)) {
    return crewBidsRequests.get(cacheKey);
  }

  dispatch(fetchCrewBidsRequest());

  // Try crew_name first (for entity-based sharing), fallback to crew_id
  const isNumericId = /^\d+$/.test(crewIdOrName);
  const fetchParams = isNumericId ? { crew_id: crewIdOrName } : { crew_name: crewIdOrName };

  const requestPromise = getBidsAPI(fetchParams)
    .then(response => {
      const bids = response?.bids || response?.data?.bids || [];
      dispatch(fetchCrewBidsSuccess(cacheKey, bids));
      return bids;
    })
    .catch(e => {
      log.error(e, 'fetch-crew-bids-failed');
      dispatch(fetchCrewBidsError(storableError(e)));
      throw e;
    })
    .finally(() => {
      crewBidsRequests.delete(cacheKey);
    });

  crewBidsRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

/**
 * Create a new bid
 */
export const createBid = bidData => (dispatch, getState) => {
  dispatch(createBidRequest());

  return createBidAPI(bidData)
    .then(response => {
      const bid = response?.bid || response?.data?.bid || response;
      dispatch(createBidSuccess(bid));
      return bid;
    })
    .catch(e => {
      log.error(e, 'create-bid-failed');
      dispatch(createBidError(storableError(e)));
      throw e;
    });
};

/**
 * Update an existing bid
 */
export const updateBid = (bidId, bidData) => (dispatch, getState) => {
  dispatch(updateBidRequest());

  return updateBidAPI(bidId, bidData)
    .then(response => {
      const bid = response?.bid || response?.data?.bid || response;
      dispatch(updateBidSuccess(bid));
      return bid;
    })
    .catch(e => {
      log.error(e, 'update-bid-failed');
      dispatch(updateBidError(storableError(e)));
      throw e;
    });
};

/**
 * Delete a bid
 */
export const deleteBid = bidId => (dispatch, getState) => {
  dispatch(deleteBidRequest());

  return deleteBidAPI(bidId)
    .then(response => {
      const deletedBid = response?.bid || { id: bidId, status: 'deleted' };
      dispatch(deleteBidSuccess(deletedBid));
      return deletedBid;
    })
    .catch(e => {
      log.error(e, 'delete-bid-failed');
      dispatch(deleteBidError(storableError(e)));
      throw e;
    });
};
